import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ModeCountdowns from "@/components/ModeCountdowns";
import {
  ArrowRight, Broadcast, Stairs, Sword, Fire, Star, Crown,
  GameController, CaretDown, CaretUp,
} from "@phosphor-icons/react";

const ALL_MODES = [
  {
    to: "/daily", Icon: Broadcast, name: "NK Daily",
    headline: "Twice a day.", sub: "Live show — 15 questions, fastest correct wins.",
    modeKey: "daily", featured: true,
  },
  {
    to: "/ladder", Icon: Stairs, name: "NK Ladder",
    headline: "Climb 15 rungs.", sub: "Cash out, or push higher. Safe floors at 5 and 10.",
    modeKey: "ladder",
  },
  {
    to: "/duel", Icon: Sword, name: "NK Duel",
    headline: "Head-to-head.", sub: "5 questions vs one player. Best score takes it.",
    modeKey: "duel",
  },
  {
    to: "/survival", Icon: Fire, name: "NK Escape",
    headline: "One miss. Out.", sub: "Chase the milestone — 10, 25, 50, 100 in a row.",
    modeKey: "survival",
  },
  {
    to: "/saturday", Icon: Star, name: "Saturday Challenge",
    headline: "Weekly big show.", sub: "The headline event every Saturday.",
    modeKey: "saturday", gold: true,
  },
  {
    to: "/championship", Icon: Crown, name: "Championship",
    headline: "Day 31 finals.", sub: "Top of every country meets in the live bracket.",
    modeKey: "championship", gold: true,
  },
];

export default function PlayHub() {
  const [modes, setModes] = useState({});
  const [engagement, setEngagement] = useState(null);
  const [celebrateChampion, setCelebrateChampion] = useState(false);

  useEffect(() => {
    api.get("/modes/status").then((r) => setModes(r.data)).catch(() => {});
    api.get("/engagement/me").then((r) => {
      setEngagement(r.data);
      if (r.data.daily_champion && !r.data.daily_champion_celebrated) {
        setCelebrateChampion(true);
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-7 max-w-5xl" data-testid="play-hub">
      {/* Hero */}
      <header>
        <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold/85 font-medium mb-2 flex items-center gap-2">
          <GameController size={13} weight="fill" />
          Pick a mode
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
          What do you want to <span className="italic font-light text-nk-gold">play?</span>
        </h1>
      </header>

      {/* Next-up countdowns */}
      <ModeCountdowns />

      {/* Daily Champion celebration (once per day) */}
      {celebrateChampion && (
        <ChampionCelebration
          country={engagement?.country || ""}
          bonus={engagement?.daily_champion_ss || 2000}
          onDismiss={() => {
            setCelebrateChampion(false);
            api.post("/engagement/champion-celebrated").catch(() => {});
          }}
        />
      )}

      {/* Compact Today strip — folds missions progress + combo + top streak */}
      {engagement && <TodayStrip data={engagement} />}

      {/* Modes grid — NK Daily is now the first (larger) tile, no separate Featured section */}
      <section>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium mb-3">All modes</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {ALL_MODES.map((m) => (
            <RichTile key={m.modeKey} mode={m} status={modes[m.modeKey]}
                      featured={m.featured} />
          ))}
        </div>
      </section>
    </div>
  );
}

function RichTile({ mode, status, featured = false }) {
  const disabled = status?.enabled === false;
  const Icon = mode.Icon;
  if (disabled) {
    return (
      <div
        className={`nk-card p-5 opacity-50 cursor-not-allowed ${featured ? "sm:col-span-2" : ""}`}
        data-testid={`play-tile-${mode.modeKey}-disabled`}
      >
        <Icon size={22} weight="duotone" className="text-white/40 mb-2" />
        <p className="font-display text-lg font-medium line-through decoration-nk-bad/40 text-white/80">{mode.name}</p>
        <p className="text-[10px] text-nk-bad mt-1">{status?.reason || "Offline"}</p>
      </div>
    );
  }
  // Featured card = spans both columns, larger icon, gold border accent
  const featuredCls = featured
    ? "sm:col-span-2 p-6 sm:p-7 border-nk-gold/45 bg-gradient-to-br from-nk-green/20 via-transparent to-nk-gold/[0.06]"
    : "p-5 sm:p-6";
  const iconSize = featured ? 28 : 24;
  const iconBoxSize = featured ? "w-14 h-14" : "w-12 h-12";
  const nameSize = featured ? "text-2xl" : "text-lg";
  return (
    <Link
      to={mode.to}
      data-testid={`play-tile-${mode.modeKey}`}
      className={`nk-card nk-card-lift group relative overflow-hidden ${featuredCls} ${
        mode.gold ? "border-nk-gold/45 bg-gradient-to-br from-nk-gold/10 to-transparent" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`shrink-0 ${iconBoxSize} rounded-md flex items-center justify-center transition-colors ${
          mode.gold || featured ? "bg-nk-gold/15 group-hover:bg-nk-gold/30" : "bg-nk-green/35 group-hover:bg-nk-green/55"
        }`}>
          <Icon size={iconSize} weight={mode.gold || featured ? "fill" : "duotone"} className="text-nk-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-display font-medium leading-tight ${nameSize} ${mode.gold ? "text-nk-gold" : "text-white"}`}>
            {mode.name}
          </p>
          <p className={`font-display italic font-light mt-0.5 ${featured ? "text-base text-nk-gold" : "text-sm text-nk-gold/80"}`}>
            {mode.headline}
          </p>
          <p className={`text-white/60 mt-2 leading-relaxed ${featured ? "text-sm max-w-md" : "text-xs"}`}>
            {mode.sub}
          </p>
          <p className={`text-xs mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all font-medium ${
            mode.gold || featured ? "text-nk-gold" : "text-white/80"
          }`}>
            Play <ArrowRight size={12} weight="bold" />
          </p>
        </div>
      </div>
    </Link>
  );
}



/**
 * NKognito 2.0 — Compact "Today" strip.
 *
 * Replaces the older 3-block EngagementPanel with a single horizontal card:
 *   ┌ [Missions 3/4]  [Combo 2/4 modes · +250]  [Top streak 🔥 5 Daily]  [Show missions ▾] ┐
 *
 * Streaks at zero are hidden (avoids "0 0 0 0" noise for new players).
 * Full mission list expands as an accordion. Champion pill shows only when active.
 */
function TodayStrip({ data }) {
  const [expanded, setExpanded] = useState(false);
  const missionsDone = data.missions.filter((m) => m.complete).length;
  const missionsTotal = data.missions.length;
  const allMissionsDone = missionsDone === missionsTotal;

  const combo = data.combo_tier_claimed || 0;
  const modesPlayed = (data.modes_played || []).length;
  const combos = data.combo_bonuses || {};
  const nextComboTier = [2, 3, 4].find((t) => t > combo);
  const nextComboAmount = nextComboTier ? combos[nextComboTier] || 0 : null;

  // Only show streaks > 0 to reduce noise; pick the biggest alive one.
  const aliveStreaks = (data.streaks || [])
    .filter((s) => s.current > 0)
    .sort((a, b) => b.current - a.current);
  const topStreak = aliveStreaks[0];

  return (
    <section className="rounded-md border border-white/8 bg-white/[0.02]" data-testid="today-strip">
      {/* Compact top row */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3">
        {/* Missions pill */}
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid="today-missions-toggle"
          className="flex items-center gap-2 text-sm group"
        >
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">Missions</span>
          <span className={`font-mono ${allMissionsDone ? "text-nk-gold" : "text-white"}`}>
            {missionsDone}/{missionsTotal}
          </span>
          <span className="text-white/40 group-hover:text-nk-gold transition-colors">
            {expanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
          </span>
          {allMissionsDone && data.missions_bonus_claimed && (
            <span className="text-[10px] text-nk-gold">✓ +{data.missions_bonus_ss.toLocaleString()}</span>
          )}
        </button>

        {/* Combo pill */}
        <div className="flex items-center gap-2 text-sm" data-testid="today-combo">
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">Combo</span>
          <span className="font-mono text-white">{modesPlayed}/4 modes</span>
          {combo > 0 && (
            <span className="text-[10px] text-nk-gold">
              +{(combos[combo] || 0).toLocaleString()} banked
            </span>
          )}
          {nextComboTier && nextComboAmount > 0 && (
            <span className="text-[10px] text-white/45">
              · next tier +{nextComboAmount.toLocaleString()}
            </span>
          )}
        </div>

        {/* Top streak — hidden entirely when all 0 */}
        {topStreak && (
          <div className="flex items-center gap-2 text-sm" data-testid="today-top-streak">
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">Streak</span>
            <span>{topStreak.icon}</span>
            <span className="font-mono text-nk-gold">{topStreak.current}</span>
            <span className="text-[10px] text-white/50 uppercase">{topStreak.label.replace("NK ", "")}</span>
          </div>
        )}

        {/* Daily Champion pill (only if active today) */}
        {data.daily_champion && (
          <div className="flex items-center gap-1.5 text-sm ml-auto" data-testid="today-champion">
            <span className="text-lg">🏆</span>
            <span className="text-[11px] font-mono uppercase tracking-widest text-nk-gold">
              Champion +{data.daily_champion_ss}
            </span>
          </div>
        )}

        {/* Knowledge Coverage link — always visible, right side */}
        <Link
          to="/coverage"
          data-testid="today-coverage-link"
          className={`text-[11px] font-mono uppercase tracking-widest text-white/60 hover:text-nk-gold inline-flex items-center gap-1 ${data.daily_champion ? "" : "ml-auto"}`}
        >
          Knowledge <ArrowRight size={11} weight="bold" />
        </Link>
      </div>

      {/* Expanded missions list */}
      {expanded && (
        <div className="border-t border-white/8 px-4 py-3" data-testid="today-missions-list">
          <ul className="space-y-1.5">
            {data.missions.map((m) => (
              <li key={m.id} data-testid={`mission-${m.id}`}
                  className={`flex items-center justify-between px-2.5 py-2 rounded-sm text-sm border ${
                    m.complete ? "border-nk-gold/40 bg-nk-gold/[0.06] text-nk-gold" :
                    "border-white/8 text-white/80"
                  }`}>
                <span className="flex items-center gap-2">
                  <span>{m.complete ? "✓" : "○"}</span>
                  {m.label}
                </span>
                <span className="font-mono text-xs text-white/60">{m.progress}/{m.target}</span>
              </li>
            ))}
          </ul>
          {allMissionsDone && !data.missions_bonus_claimed && (
            <p className="text-xs text-nk-gold mt-3">Bonus awarded automatically after next mode play.</p>
          )}
        </div>
      )}
    </section>
  );
}


/**
 * NKognito 2.0 — Daily Champion Celebration
 * Fired once per day the moment a player completes all 4 modes + all missions.
 * Full-screen modal with confetti-style pattern, gold accents, and a single CTA.
 */
function ChampionCelebration({ country, bonus, onDismiss }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm transition-opacity ${
      visible ? "opacity-100" : "opacity-0"
    }`} data-testid="champion-celebration" onClick={onDismiss}>
      {/* Animated kente-inspired ring pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-30"
           style={{
             backgroundImage: "repeating-conic-gradient(from 0deg at 50% 50%, #F5C518 0deg 8deg, transparent 8deg 20deg, #F5C518 20deg 22deg, transparent 22deg 40deg)",
             animation: "spin 60s linear infinite",
           }} />

      <div className={`relative max-w-md mx-4 rounded-md border-2 border-nk-gold bg-nk-bg p-8 text-center transition-all duration-500 ${
        visible ? "scale-100 opacity-100" : "scale-90 opacity-0"
      }`} onClick={(e) => e.stopPropagation()}>
        <div className="text-6xl mb-3 animate-bounce">🏆</div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-nk-gold font-mono mb-2">
          Daily Champion
        </p>
        <h2 className="font-display text-3xl text-white leading-tight">
          You are today&apos;s{" "}
          <span className="text-nk-gold">Daily Champion</span>
          {country && <> in {country}</>}!
        </h2>
        <p className="text-sm text-white/70 mt-4">
          All 4 modes played · All 4 missions complete
        </p>

        <div className="mt-6 py-4 px-5 rounded-sm border border-nk-gold/40 bg-nk-gold/[0.08]">
          <p className="text-[10px] font-mono uppercase text-nk-gold/70 mb-1">Bonus awarded</p>
          <p className="font-display text-3xl text-nk-gold">+{bonus.toLocaleString()} SS</p>
          <p className="text-[11px] text-white/60 mt-2">Plus double XP · Leaderboard highlight active</p>
        </div>

        <button
          onClick={onDismiss}
          data-testid="champion-celebration-dismiss"
          className="mt-6 w-full px-4 py-3 rounded-sm bg-nk-gold text-nk-bg font-medium text-sm hover:opacity-90 transition-opacity"
        >
          Keep the crown
        </button>
      </div>
    </div>
  );
}

